import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Target, AlertTriangle, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import { startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear } from 'date-fns';

interface InsightsWidgetProps {
  transactions: any[];
  accounts: any[];
  monthOverMonthChange: number;
  savingsRate: number;
  dailyAvgSpending: number;
  formatCurrencyWithAbbrev: (amount: number) => string;
  safeT: (key: string) => string;
  selectedPeriod: string;
}

interface Insight {
  id: string;
  type: 'positive' | 'warning' | 'info';
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: string;
}

export const InsightsWidget: React.FC<InsightsWidgetProps> = ({
  transactions,
  monthOverMonthChange,
  savingsRate,
  dailyAvgSpending,
  formatCurrencyWithAbbrev,
  safeT,
  selectedPeriod
}) => {
  const insights = useMemo((): Insight[] => {
    const generatedInsights: Insight[] = [];

    // Get date range for selected period
    const getDateRange = () => {
      const now = new Date();
      
      switch (selectedPeriod) {
        case 'thisMonth':
          return {
            start: startOfMonth(now),
            end: endOfMonth(now)
          };
        case 'lastMonth':
          const lastMonth = subMonths(now, 1);
          return {
            start: startOfMonth(lastMonth),
            end: endOfMonth(lastMonth)
          };
        case 'last3Months':
          return {
            start: startOfMonth(subMonths(now, 2)),
            end: endOfMonth(now)
          };
        case 'last6Months':
          return {
            start: startOfMonth(subMonths(now, 5)),
            end: endOfMonth(now)
          };
        case 'thisYear':
          return {
            start: startOfYear(now),
            end: endOfYear(now)
          };
        case 'allTime':
          return {
            start: new Date(0),
            end: now
          };
        default:
          return {
            start: startOfMonth(now),
            end: endOfMonth(now)
          };
      }
    };

    const { start: periodStart, end: periodEnd } = getDateRange();

    // Spending trend insight
    if (monthOverMonthChange > 10) {
      generatedInsights.push({
        id: 'spending-increase',
        type: 'warning',
        icon: <TrendingUp className="h-4 w-4" />,
        title: safeT('dashboard.insights.spendingIncrease'),
        description: safeT('dashboard.insights.spendingIncreaseDesc').replace('{percentage}', monthOverMonthChange.toFixed(1)),
        action: safeT('dashboard.insights.actions.reviewExpenses')
      });
    } else if (monthOverMonthChange < -10) {
      generatedInsights.push({
        id: 'spending-decrease',
        type: 'positive',
        icon: <TrendingDown className="h-4 w-4" />,
        title: safeT('dashboard.insights.spendingDecrease'),
        description: safeT('dashboard.insights.spendingDecreaseDesc').replace('{percentage}', Math.abs(monthOverMonthChange).toFixed(1)),
        action: safeT('dashboard.insights.actions.keepGoing')
      });
    }

    // Savings rate insight
    if (savingsRate < 10) {
      generatedInsights.push({
        id: 'low-savings',
        type: 'warning',
        icon: <AlertTriangle className="h-4 w-4" />,
        title: safeT('dashboard.insights.lowSavings'),
        description: safeT('dashboard.insights.lowSavingsDesc').replace('{rate}', savingsRate.toFixed(1)),
        action: safeT('dashboard.insights.actions.reduceExpenses')
      });
    } else if (savingsRate > 20) {
      generatedInsights.push({
        id: 'good-savings',
        type: 'positive',
        icon: <Target className="h-4 w-4" />,
        title: safeT('dashboard.insights.goodSavings'),
        description: safeT('dashboard.insights.goodSavingsDesc').replace('{rate}', savingsRate.toFixed(1)),
        action: safeT('dashboard.insights.actions.considerInvesting')
      });
    }

    // Spending projection insight - adjusted for period
    const getPeriodDays = () => {
      switch (selectedPeriod) {
        case 'thisMonth':
        case 'lastMonth':
          return 30;
        case 'last3Months':
          return 90;
        case 'last6Months':
          return 180;
        case 'thisYear':
          return 365;
        case 'allTime':
          return 365; // Use yearly projection for all time
        default:
          return 30;
      }
    };

    const periodDays = getPeriodDays();
    const periodProjection = dailyAvgSpending * periodDays;
    
    if (periodProjection > 0) {
      const projectionText = selectedPeriod === 'allTime' ? 'yearly' : 
                           selectedPeriod === 'thisYear' ? 'yearly' :
                           selectedPeriod === 'last6Months' ? 'semi-annually' :
                           selectedPeriod === 'last3Months' ? 'quarterly' : 'monthly';
      
      generatedInsights.push({
        id: 'spending-projection',
        type: 'info',
        icon: <Lightbulb className="h-4 w-4" />,
        title: safeT('dashboard.insights.spendingProjection'),
        description: `At current rate, you'll spend ${formatCurrencyWithAbbrev(periodProjection)} this ${projectionText.replace('ly', '')}`,
        action: safeT('dashboard.insights.actions.trackWeekly')
      });
    }

    // Transaction frequency insight - adjusted for period
    const getRecentDays = () => {
      switch (selectedPeriod) {
        case 'thisMonth':
        case 'lastMonth':
          return 7; // Check last week
        case 'last3Months':
          return 14; // Check last 2 weeks
        case 'last6Months':
          return 21; // Check last 3 weeks
        case 'thisYear':
        case 'allTime':
          return 30; // Check last month
        default:
          return 7;
      }
    };

    const recentDays = getRecentDays();
    const recentTransactions = transactions.filter(t => 
      new Date(t.date) >= new Date(Date.now() - recentDays * 24 * 60 * 60 * 1000) &&
      new Date(t.date) >= periodStart &&
      new Date(t.date) <= periodEnd
    );
    
    if (recentTransactions.length === 0) {
      const timeFrame = recentDays === 7 ? 'week' : recentDays === 14 ? '2 weeks' : 'month';
      generatedInsights.push({
        id: 'no-recent-activity',
        type: 'info',
        icon: <Lightbulb className="h-4 w-4" />,
        title: `Quiet ${timeFrame}`,
        description: `No transactions recorded in the past ${timeFrame}`,
        action: safeT('dashboard.insights.actions.logTransactions')
      });
    }

    return generatedInsights.slice(0, 3);
  }, [transactions, monthOverMonthChange, savingsRate, dailyAvgSpending, formatCurrencyWithAbbrev, selectedPeriod]);

  // Get period label for the header
  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'thisMonth': return 'This Month';
      case 'lastMonth': return 'Last Month';
      case 'last3Months': return 'Last 3 Months';
      case 'last6Months': return 'Last 6 Months';
      case 'thisYear': return 'This Year';
      case 'allTime': return 'All Time';
      default: return 'Current Period';
    }
  };

  if (insights.length === 0) {
    return (
      <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
        <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
          <div className="flex items-center">
            <Lightbulb className="h-5 w-5 text-secondary dark:text-primary-dark mr-2 rtl:ml-2 rtl:mr-0" />
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {safeT('dashboard.insights.title')} - {getPeriodLabel()}
            </h3>
          </div>
          <div className="text-xs px-2 py-1 rounded-full bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark">
            {safeT('dashboard.insights.aiPowered')}
          </div>
        </div>
        <div className="p-6">
          <p className="text-text-secondary dark:text-text-dark-secondary text-sm">
            {safeT('dashboard.insights.noInsights')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
      <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
        <div className="flex items-center">
          <Lightbulb className="h-5 w-5 text-secondary dark:text-primary-dark mr-2 rtl:ml-2 rtl:mr-0" />
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {safeT('dashboard.insights.title')} - {getPeriodLabel()}
          </h3>
        </div>
        <div className="text-xs px-2 py-1 rounded-full bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark">
          {safeT('dashboard.insights.aiPowered')}
        </div>
      </div>
      
      <div className="p-6">
        <div className="space-y-3">
          {insights.map((insight) => (
          <div
            key={insight.id}
            className={cn(
              "p-4 rounded-lg border-l-4 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150",
              insight.type === 'positive' && "border-l-positive dark:border-l-positive-dark",
              insight.type === 'warning' && "border-l-negative dark:border-l-negative-dark",
              insight.type === 'info' && "border-l-secondary dark:border-l-primary-dark"
            )}
          >
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <div className={cn(
                "flex-shrink-0 p-1 rounded",
                insight.type === 'positive' && "text-positive dark:text-positive-dark",
                insight.type === 'warning' && "text-negative dark:text-negative-dark",
                insight.type === 'info' && "text-secondary dark:text-primary-dark"
              )}>
                {insight.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {insight.title}
                </p>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                  {insight.description}
                </p>
                {insight.action && (
                  <p className="text-xs text-secondary dark:text-primary-dark mt-2 font-medium">
                    💡 {insight.action}
                  </p>
                )}
              </div>
            </div>
          </div>
          ))}
        </div>
      </div>
    </div>
  );
}; 